import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
} from '@heroui/react'

import {
  BridgeUnavailableError,
  listEvalFixtures,
  listEvalResults,
  getEvalResult,
  type FixtureListItem,
} from '../services'
import type {
  FixtureMeta,
  ReportJson,
} from '../../../../functions/tests/eval/recommendations/types'

/** Per-fixture rollup joined from the latest eval results JSON, if any. */
interface ResultsJoin {
  /** Best judge `overall` across presets (null when unjudged). */
  bestJudgeOverall: number | null
  trapCount: number
  unlabeledCount: number
}

interface FixtureLibraryProps {
  /** Open an existing fixture into the builder. */
  onOpen: (meta: FixtureMeta) => void
  /** Start the blank-slate create flow. */
  onCreateNew: () => void
}

function snippetOf(meta: FixtureMeta): string {
  const s = meta.request?.context?.conversationSnippet?.trim()
  if (s) return s
  const intent = meta.request?.context?.userIntent?.trim()
  return intent ? `Intent: ${intent}` : '(no conversation)'
}

/**
 * Build a per-fixture rollup from a results report: best judge `overall` across
 * presets, total trap count, and distinct unlabeled-pick count. The report's
 * `unlabeledIds` are a snapshot from when the eval ran — `labeledByFixture`
 * carries each fixture's CURRENT buckets so picks labeled since then (e.g. in
 * a triage session) drop out of the count.
 */
function joinResults(
  report: ReportJson,
  labeledByFixture: Map<string, Set<string>>
): Map<string, ResultsJoin> {
  const byFixture = new Map<string, ResultsJoin>()
  for (const f of report.fixtures) {
    const runs = report.results.filter((r) => r.fixtureId === f.id)
    if (runs.length === 0) {
      byFixture.set(f.id, {
        bestJudgeOverall: null,
        trapCount: 0,
        unlabeledCount: 0,
      })
      continue
    }
    let bestJudgeOverall: number | null = null
    let trapCount = 0
    const unlabeled = new Set<string>()
    const labeled = labeledByFixture.get(f.id) ?? new Set<string>()
    for (const r of runs) {
      if (r.score) trapCount += r.score.trapCount
      if (r.judge && r.judge.overall !== null) {
        bestJudgeOverall =
          bestJudgeOverall === null
            ? r.judge.overall
            : Math.max(bestJudgeOverall, r.judge.overall)
      }
      for (const id of r.unlabeledIds) {
        if (!labeled.has(id)) unlabeled.add(id)
      }
    }
    byFixture.set(f.id, {
      bestJudgeOverall,
      trapCount,
      unlabeledCount: unlabeled.size,
    })
  }
  return byFixture
}

export function FixtureLibrary({ onOpen, onCreateNew }: FixtureLibraryProps) {
  const [fixtures, setFixtures] = useState<FixtureListItem[]>([])
  const [join, setJoin] = useState<Map<string, ResultsJoin>>(new Map())
  const [latestRunLabel, setLatestRunLabel] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [bridgeDown, setBridgeDown] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setBridgeDown(false)
    try {
      const list = await listEvalFixtures()
      setFixtures(list)
      // Best-effort join against the newest results file.
      try {
        const results = await listEvalResults()
        if (results.length > 0) {
          const latest = await getEvalResult(results[0].file)
          const labeledByFixture = new Map(
            list.map((f) => [
              f.meta.id ?? f.slug,
              new Set([
                ...(f.meta.expected?.traps ?? []),
                ...(f.meta.expected?.forbidden ?? []),
                ...(f.meta.expected?.good ?? []),
              ]),
            ])
          )
          setJoin(joinResults(latest, labeledByFixture))
          setLatestRunLabel(results[0].file.replace(/\.json$/, ''))
        } else {
          setJoin(new Map())
          setLatestRunLabel(null)
        }
      } catch {
        // Results are optional; a fixture list with no results still renders.
        setJoin(new Map())
        setLatestRunLabel(null)
      }
    } catch (err) {
      if (err instanceof BridgeUnavailableError) {
        setBridgeDown(true)
      } else {
        setError(err instanceof Error ? err.message : String(err))
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const sorted = useMemo(
    () => [...fixtures].sort((a, b) => a.slug.localeCompare(b.slug)),
    [fixtures]
  )

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Fixture library</h2>
          <p className="text-xs text-default-500">
            {latestRunLabel
              ? `Judge/traps/unlabeled joined from ${latestRunLabel}`
              : 'Existing recommendation-eval fixtures on disk'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="flat" onPress={() => void load()}>
            Refresh
          </Button>
          <Button size="sm" color="primary" onPress={onCreateNew}>
            Create new
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        {loading && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}

        {!loading && bridgeDown && (
          <div className="p-3 rounded bg-warning-50 dark:bg-warning-950/30 text-warning-700 dark:text-warning-300 text-sm">
            Fixture file access requires the dev server. Run{' '}
            <code className="text-xs">npm run dev</code> to browse, open, and
            save fixtures. You can still use “Create new” to author one and copy
            the JSON.
          </div>
        )}

        {!loading && error && (
          <div className="p-3 rounded bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {!loading && !bridgeDown && !error && sorted.length === 0 && (
          <p className="text-sm text-default-500">
            No fixtures on disk yet. Click “Create new” to author the first one.
          </p>
        )}

        {!loading && !bridgeDown && !error && sorted.length > 0 && (
          <div className="space-y-2">
            {sorted.map(({ slug, meta }) => {
              const j = join.get(meta.id)
              const traps = meta.expected?.traps?.length ?? 0
              const forbidden = meta.expected?.forbidden?.length ?? 0
              const good = meta.expected?.good?.length ?? 0
              const gold = meta.expected?.gold?.length ?? 0
              const hasNote = Boolean(meta.expected?.note?.trim())
              return (
                <div
                  key={slug}
                  className="flex flex-col gap-2 p-3 rounded-lg border border-default-200 dark:border-default-100/20"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono text-xs text-default-500 truncate">
                        {slug}
                      </p>
                      <p className="text-sm font-medium mt-0.5 line-clamp-2 whitespace-pre-wrap">
                        {snippetOf(meta)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      color="primary"
                      variant="flat"
                      onPress={() => onOpen(meta)}
                    >
                      Open
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    <Chip
                      size="sm"
                      color={hasNote ? 'primary' : 'default'}
                      variant="flat"
                    >
                      {hasNote ? 'note ✓' : 'no note'}
                    </Chip>
                    <Chip size="sm" color="warning" variant="flat">
                      traps {traps}
                    </Chip>
                    <Chip size="sm" color="danger" variant="flat">
                      forbidden {forbidden}
                    </Chip>
                    {good > 0 && (
                      <Chip size="sm" color="success" variant="flat">
                        known-good {good}
                      </Chip>
                    )}
                    {/* Always rendered (even at 0) so fixtures still awaiting a
                        gold-marking pass stand out in the list. */}
                    <Chip
                      size="sm"
                      color={gold > 0 ? 'warning' : 'default'}
                      variant={gold > 0 ? 'solid' : 'bordered'}
                    >
                      {gold > 0 ? `★ ${gold}` : '☆ 0'}
                    </Chip>
                    {j && (
                      <>
                        <span className="mx-1 text-default-300">·</span>
                        <Chip size="sm" variant="bordered">
                          judge{' '}
                          {j.bestJudgeOverall === null
                            ? '—'
                            : `${j.bestJudgeOverall}/3`}
                        </Chip>
                        <Chip
                          size="sm"
                          variant="bordered"
                          color={j.trapCount > 0 ? 'warning' : 'default'}
                        >
                          traps hit {j.trapCount}
                        </Chip>
                        {j.unlabeledCount > 0 && (
                          <Chip size="sm" color="secondary" variant="flat">
                            {j.unlabeledCount} unlabeled pick
                            {j.unlabeledCount === 1 ? '' : 's'}
                          </Chip>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardBody>
    </Card>
  )
}
